import { browserLocalStorage, type StoragePort } from './persistence.js';

/**
 * Turns the device performance test into graphics settings. Everything here is
 * pure (no WebGL, no DOM) so the scoring rules are unit tested; the test
 * drive itself lives in graphics-benchmark.ts.
 */

export type CalibrationTier = 'low' | 'medium' | 'high' | 'ultra';
export const CALIBRATION_TIERS: readonly CalibrationTier[] = ['low', 'medium', 'high', 'ultra'];

export interface CalibrationStage {
  tier: CalibrationTier;
  /** Median GPU+driver milliseconds for one proxy frame at the tier's settings. */
  frameMs: number;
  pixelRatio: number;
  /** Backbuffer pixels the stage rendered. */
  pixels: number;
  /** GPU timer queries when available; otherwise wall time around a GPU flush. */
  timing?: 'gpu-timer' | 'flush';
}

export interface CalibrationFeatures {
  bloom: boolean;
  shadows: boolean;
  screenDistortion: boolean;
  /** Ambient occlusion and wet-road reflections (high/ultra). */
  screenSpaceLighting: boolean;
  /** Live reflection probe on cars (high/ultra). */
  carReflections: boolean;
}

export interface GraphicsCalibration {
  version: 1;
  status: 'measured';
  measuredAt: number;
  tier: CalibrationTier;
  frameRateTarget: 'smooth' | 'battery';
  /** Starting resolution scale for the adaptive governor (1 = native). */
  renderScale: number;
  features: CalibrationFeatures;
  /** Expected race frame rate at the recommended tier, before adaptive tuning. */
  estimatedFps: number;
  gpuScore: number;
  cpuScore: number;
  stages: CalibrationStage[];
  renderer: string;
  viewport: { width: number; height: number; devicePixelRatio: number };
}

export interface SkippedCalibration {
  version: 1;
  status: 'skipped';
  measuredAt: number;
}

export type StoredCalibration = GraphicsCalibration | SkippedCalibration;

export interface CalibrationInput {
  stages: CalibrationStage[];
  /** Best-of-N time for the fixed CPU workload, in ms. */
  cpuMs: number;
  touchDevice: boolean;
  renderer: string;
  viewport: { width: number; height: number; devicePixelRatio: number };
  now: number;
}

/**
 * Race frames cost more GPU time than the proxy drive: the real world has
 * more materials, the custom road shader, particles and traffic. Measured on
 * Neon District at night (the heaviest map) with GPU timer queries against
 * the proxy on the same machine (Iris Xe, 1920×859): race LOW 12.1 ms,
 * MEDIUM 18.1 ms, HIGH 30.3 ms at full resolution; proxy 6.3 / 9.4 / 20.7 ms.
 */
export const RACE_COST_FACTOR: Readonly<Record<CalibrationTier, number>> = {
  low: 1.9,
  medium: 1.95,
  high: 1.5,
  ultra: 1.5,
};

/**
 * Browser compositing (canvas, HUD, page layers) happens after the GPU timer
 * stops and still has to fit into the same vsync interval.
 */
export const COMPOSITOR_MS = 4;
export const FRAME_BUDGET_MS = 1_000 / 60;
/** The adaptive governor's resolution steps a tier may start from. */
export const RENDER_SCALES: readonly number[] = [1, 0.88, 0.8, 0.72];
/**
 * How soft each tier may start. LOW and MEDIUM may trade a little sharpness
 * (MEDIUM keeps bloom, the neon look's backbone); the premium tiers only pay
 * off near native resolution, where their MSAA and reflections are visible.
 */
const MINIMUM_START_SCALE: Readonly<Record<CalibrationTier, number>> = {
  low: 0.72,
  medium: 0.72,
  high: 0.8,
  ultra: 0.88,
};
/** Wall-clock flush timing includes a pipeline round trip the timer never sees. */
const FLUSH_OVERHEAD_MS = 2.2;
/** Reference workload time on a mid-range 2021 laptop (score 100). */
export const CPU_REFERENCE_MS = 24;
/** Reference proxy cost per megapixel on MEDIUM for GPU score 1000. */
const GPU_REFERENCE_MS_PER_MEGAPIXEL = 5.9;

/** Estimated race GPU milliseconds at full resolution. */
export function estimatedRaceFrameMs(stage: CalibrationStage): number {
  const proxyMs = stage.timing === 'flush'
    ? Math.max(stage.frameMs * 0.6, stage.frameMs - FLUSH_OVERHEAD_MS)
    : stage.frameMs;
  return proxyMs * RACE_COST_FACTOR[stage.tier];
}

/** Shadows, geometry and fixed passes do not shrink with the backbuffer. */
export function scaledRaceFrameMs(stage: CalibrationStage, renderScale: number): number {
  return estimatedRaceFrameMs(stage) * (0.3 + 0.7 * renderScale * renderScale);
}

/** Expected displayed frame rate (uncapped) at a render scale. */
export function estimatedStageFps(stage: CalibrationStage, renderScale = 1): number {
  return 1_000 / (scaledRaceFrameMs(stage, renderScale) + COMPOSITOR_MS);
}

/** The sharpest starting resolution at which a tier holds 60 FPS, or null. */
export function smoothRenderScale(stage: CalibrationStage): number | null {
  for (const scale of RENDER_SCALES) {
    if (scale < MINIMUM_START_SCALE[stage.tier]) break;
    if (estimatedStageFps(stage, scale) >= 59.5) return scale;
  }
  return null;
}

export function recommendCalibration(input: CalibrationInput): GraphicsCalibration {
  const byTier = new Map(input.stages.map((stage) => [stage.tier, stage]));
  const cpuScore = Math.max(1, Math.round((CPU_REFERENCE_MS / Math.max(0.1, input.cpuMs)) * 100));
  let tierIndex = 0;
  for (let index = 0; index < CALIBRATION_TIERS.length; index += 1) {
    const stage = byTier.get(CALIBRATION_TIERS[index]!);
    if (!stage || smoothRenderScale(stage) === null) break;
    tierIndex = index;
  }
  // A slow main thread feels worse than a soft image: the higher tiers add
  // scenery, rival detail and effect emission that all cost simulation time.
  if (cpuScore < 45 && tierIndex > 1) tierIndex -= 1;
  // Phones and tablets throttle within minutes; ULTRA's extra samples rarely
  // survive a full race and only cost battery.
  if (input.touchDevice && tierIndex > 2) tierIndex = 2;
  const tier = CALIBRATION_TIERS[tierIndex]!;
  const chosen = byTier.get(tier);
  const lowStage = byTier.get('low');
  // Not even LOW at the softest start holds 60: a steady 30 beats a jittery 45.
  const frameRateTarget = lowStage && estimatedStageFps(lowStage, RENDER_SCALES[RENDER_SCALES.length - 1]!) < 50
    ? 'battery'
    : 'smooth';
  const renderScale = chosen ? smoothRenderScale(chosen) ?? RENDER_SCALES[RENDER_SCALES.length - 1]! : 1;
  const nativeFps = chosen ? estimatedStageFps(chosen, 1) : 0;
  const features: CalibrationFeatures = {
    bloom: tier !== 'low',
    // Optional extras need spare time at the chosen start scale.
    shadows: tier === 'high' || tier === 'ultra' || (tier === 'medium' && renderScale === 1 && nativeFps >= 72),
    screenDistortion: tier !== 'low',
    screenSpaceLighting: tier === 'ultra' || (tier === 'high' && renderScale === 1),
    carReflections: tier === 'ultra' || (tier === 'high' && renderScale >= 0.88),
  };
  const fpsCap = frameRateTarget === 'battery' ? 30 : 60;
  const estimatedFps = chosen
    ? Math.max(1, Math.min(fpsCap, Math.round(estimatedStageFps(chosen, renderScale))))
    : 0;
  return {
    version: 1,
    status: 'measured',
    measuredAt: input.now,
    tier,
    frameRateTarget,
    renderScale,
    features,
    estimatedFps,
    gpuScore: gpuScore(input.stages),
    cpuScore,
    stages: input.stages.map((stage) => ({ ...stage })),
    renderer: input.renderer.slice(0, 120),
    viewport: { ...input.viewport },
  };
}

/** The effects a quality preset enables by default (mirrors the race renderer). */
export function tierFeatureDefaults(tier: CalibrationTier): CalibrationFeatures {
  const premium = tier === 'high' || tier === 'ultra';
  return {
    bloom: tier !== 'low',
    shadows: premium,
    screenDistortion: tier !== 'low',
    screenSpaceLighting: premium,
    carReflections: premium,
  };
}

/** Whether the race renderer can draw an effect at a tier at all. */
export function tierSupportsFeature(tier: CalibrationTier, feature: keyof CalibrationFeatures): boolean {
  if (feature === 'screenSpaceLighting' || feature === 'carReflections') return tier === 'high' || tier === 'ultra';
  // LOW renders straight to the screen: no post chain, no shadow pass.
  return tier !== 'low';
}

/** Starting resolution for any tier the test measured (null when it was skipped). */
export function startScaleForTier(calibration: GraphicsCalibration, tier: CalibrationTier): number | null {
  if (tier === calibration.tier) return calibration.renderScale;
  const stage = calibration.stages.find((entry) => entry.tier === tier);
  if (!stage) return null;
  return smoothRenderScale(stage) ?? RENDER_SCALES[RENDER_SCALES.length - 1]!;
}

/** Rough share of a race frame each optional effect costs at its tier. */
const FEATURE_COST: Readonly<Partial<Record<keyof CalibrationFeatures, number>>> = {
  shadows: 0.14,
  screenSpaceLighting: 0.12,
  carReflections: 0.07,
  bloom: 0.06,
};

/**
 * Expected frame rate for a tier with the player's own effect choices: the
 * measured tier cost, plus or minus each effect that differs from the
 * preset the stage was measured with.
 */
export function estimatedCustomFps(
  stage: CalibrationStage,
  renderScale: number,
  features: CalibrationFeatures,
  frameRateTarget: 'smooth' | 'battery',
): number {
  const defaults = tierFeatureDefaults(stage.tier);
  let cost = scaledRaceFrameMs(stage, renderScale);
  for (const [feature, weight] of Object.entries(FEATURE_COST) as [keyof CalibrationFeatures, number][]) {
    if (!tierSupportsFeature(stage.tier, feature)) continue;
    const delta = Number(features[feature]) - Number(defaults[feature]);
    cost *= 1 + delta * weight;
  }
  const fps = 1_000 / (cost + COMPOSITOR_MS);
  return Math.max(1, Math.min(frameRateTarget === 'battery' ? 30 : 60, Math.round(fps)));
}

/** A resolution-independent number players can compare between devices. */
export function gpuScore(stages: readonly CalibrationStage[]): number {
  const reference = stages.find((stage) => stage.tier === 'medium') ?? stages[0];
  if (!reference || reference.pixels <= 0 || reference.frameMs <= 0) return 0;
  const msPerMegapixel = reference.frameMs / (reference.pixels / 1_000_000);
  return Math.max(1, Math.round((GPU_REFERENCE_MS_PER_MEGAPIXEL / msPerMegapixel) * 1_000));
}

/** Tiers above one that misses 60 FPS by a wide margin are not worth rendering. */
export function shouldContinueCalibration(stage: CalibrationStage): boolean {
  return estimatedStageFps(stage, RENDER_SCALES[RENDER_SCALES.length - 1]!) >= 42;
}

export const CALIBRATION_STORAGE_KEY = 'nightshift.graphics-calibration.v1';

export function parseStoredCalibration(raw: string | null): StoredCalibration | null {
  if (!raw) return null;
  try {
    const value = JSON.parse(raw) as Partial<GraphicsCalibration> & Partial<SkippedCalibration>;
    if (value?.version !== 1 || !Number.isFinite(value.measuredAt)) return null;
    if (value.status === 'skipped') return { version: 1, status: 'skipped', measuredAt: Number(value.measuredAt) };
    if (value.status !== 'measured' || !CALIBRATION_TIERS.includes(value.tier as CalibrationTier)) return null;
    const features = value.features as Partial<CalibrationFeatures> | undefined;
    if (!features) return null;
    const flag = (key: keyof CalibrationFeatures): boolean => features[key] === true;
    const stages = Array.isArray(value.stages)
      ? value.stages.filter((stage): stage is CalibrationStage => Boolean(stage)
        && CALIBRATION_TIERS.includes(stage.tier)
        && Number.isFinite(stage.frameMs)
        && Number.isFinite(stage.pixelRatio)
        && Number.isFinite(stage.pixels)
        && (stage.timing === undefined || stage.timing === 'gpu-timer' || stage.timing === 'flush'))
      : [];
    const viewport = value.viewport ?? { width: 0, height: 0, devicePixelRatio: 1 };
    return {
      version: 1,
      status: 'measured',
      measuredAt: Number(value.measuredAt),
      tier: value.tier as CalibrationTier,
      frameRateTarget: value.frameRateTarget === 'battery' ? 'battery' : 'smooth',
      renderScale: Number.isFinite(value.renderScale) ? Math.min(1, Math.max(0.5, Number(value.renderScale))) : 1,
      features: {
        bloom: flag('bloom'),
        shadows: flag('shadows'),
        screenDistortion: flag('screenDistortion'),
        screenSpaceLighting: flag('screenSpaceLighting'),
        carReflections: flag('carReflections'),
      },
      estimatedFps: Number(value.estimatedFps) || 0,
      gpuScore: Number(value.gpuScore) || 0,
      cpuScore: Number(value.cpuScore) || 0,
      stages,
      renderer: typeof value.renderer === 'string' ? value.renderer.slice(0, 120) : '',
      viewport: {
        width: Number(viewport.width) || 0,
        height: Number(viewport.height) || 0,
        devicePixelRatio: Number(viewport.devicePixelRatio) || 1,
      },
    };
  } catch {
    return null;
  }
}

let cached: { value: StoredCalibration | null } | null = null;

export function loadCalibration(storage: StoragePort = browserLocalStorage()): StoredCalibration | null {
  const value = parseStoredCalibration(storage.read(CALIBRATION_STORAGE_KEY));
  cached = { value };
  return value;
}

/** The last loaded/saved record without touching storage (render-loop safe). */
export function cachedCalibration(): StoredCalibration | null {
  if (!cached) return loadCalibration();
  return cached.value;
}

export function saveCalibration(value: StoredCalibration, storage: StoragePort = browserLocalStorage()): boolean {
  cached = { value };
  return storage.write(CALIBRATION_STORAGE_KEY, JSON.stringify(value));
}

export function clearCalibration(storage: StoragePort = browserLocalStorage()): boolean {
  cached = { value: null };
  return storage.remove(CALIBRATION_STORAGE_KEY);
}

/** Measured tier for the AUTO preset, or null to use the hardware heuristic. */
export function calibratedTier(): CalibrationTier | null {
  const value = cachedCalibration();
  return value?.status === 'measured' ? value.tier : null;
}

/** Fixed CPU workload: float math over typed arrays, close to the vehicle/AI step. */
export function runCpuProbe(rounds = 3, now: () => number = () => performance.now()): number {
  const size = 4_096;
  const positions = new Float64Array(size * 3);
  const velocities = new Float64Array(size * 3);
  for (let index = 0; index < positions.length; index += 1) {
    positions[index] = Math.sin(index * 12.9898) * 50;
    velocities[index] = Math.cos(index * 78.233);
  }
  let best = Number.POSITIVE_INFINITY;
  let checksum = 0;
  for (let round = 0; round < rounds; round += 1) {
    const startedAt = now();
    for (let step = 0; step < 60; step += 1) {
      for (let index = 0; index < size; index += 1) {
        const offset = index * 3;
        const x = positions[offset]!;
        const z = positions[offset + 2]!;
        const distance = Math.sqrt(x * x + z * z) + 1e-3;
        const pull = Math.atan2(z, x) * 0.002;
        velocities[offset] = velocities[offset]! * 0.995 - (x / distance) * 0.01 + pull;
        velocities[offset + 2] = velocities[offset + 2]! * 0.995 - (z / distance) * 0.01 - pull;
        positions[offset] = x + velocities[offset]! * 0.016;
        positions[offset + 2] = z + velocities[offset + 2]! * 0.016;
      }
    }
    best = Math.min(best, now() - startedAt);
    checksum += positions[7]!;
  }
  // Keep the loop observable so an engine cannot drop it as dead code.
  if (checksum === Number.NEGATIVE_INFINITY) console.debug(checksum);
  return best;
}
